import { useEffect, useMemo, useState } from 'react';
import type { PostSessionParticipant } from '../services/api/coachPostSession';
import type { AttendanceDraftMap } from './useCoachPostSession';

/**
 * MED-1077 — shared collapse behavior for the Attendance & Engagement section,
 * used by both the coach (`AttendanceEngagementCard`) and peer-leader
 * (`PeerLeaderAttendanceCard`) cards so they behave identically.
 *
 * Completion (MED-979): every participant has a status; Partial/Absent needs a
 * reason; Present/Partial needs an engagement rating. Notes are optional and
 * don't count.
 *
 * Collapse rule (MED-1060 AC5): the section collapses only when it is complete
 * AND saved — clean on load (server data → collapsed if complete) and after a
 * successful Save, but never on fields-set alone (which would hide the
 * bottom-right Save button). A `dirty` flag (set on edit, cleared on a
 * successful save) keeps an edited-to-complete-but-unsaved section expanded.
 * The chevron still lets the user expand a collapsed section to review/correct.
 */
export interface AttendanceSectionCollapse {
  isComplete: boolean;
  summaryLine: string;
  collapsed: boolean;
  setCollapsed: (value: boolean | ((current: boolean) => boolean)) => void;
  /** Call when a participant's attendance is edited (marks the section dirty). */
  markDirty: () => void;
  /** Awaits the save and clears `dirty` only on success (collapses if complete). */
  save: () => Promise<void>;
}

function computeIsComplete(
  participants: PostSessionParticipant[],
  attendance: AttendanceDraftMap,
  leaderUserId?: string | null
): boolean {
  if (participants.length === 0) return false;
  return participants.every(p => {
    const d = attendance[p.user_id];
    if (!d || !d.status) return false;
    if ((d.status === 'partial' || d.status === 'absent') && !d.absent_reason)
      return false;
    // MED-1163 AC5: the leader's own row is exempt from the engagement-rating
    // requirement — no rating is requested of them, so a present-but-unrated
    // leader row must not keep the section permanently expanded.
    const isLeader = leaderUserId != null && p.user_id === leaderUserId;
    if (
      !isLeader &&
      (d.status === 'attended' || d.status === 'partial') &&
      d.rating == null
    )
      return false;
    return true;
  });
}

export function useAttendanceSectionCollapse(
  participants: PostSessionParticipant[],
  attendance: AttendanceDraftMap,
  onSave: () => Promise<boolean>,
  // MED-1163 AC5: the peer-leader card passes the leader's user id so their own
  // row is exempt from the rating requirement. The coach card omits it (no
  // self-row), leaving coach completion behavior unchanged (AC7).
  leaderUserId?: string | null
): AttendanceSectionCollapse {
  const isComplete = useMemo(
    () => computeIsComplete(participants, attendance, leaderUserId),
    [participants, attendance, leaderUserId]
  );

  const summaryLine = useMemo(() => {
    let present = 0;
    let partial = 0;
    let absent = 0;
    for (const p of participants) {
      const status = attendance[p.user_id]?.status;
      if (status === 'attended') present += 1;
      else if (status === 'partial') partial += 1;
      else if (status === 'absent') absent += 1;
    }
    return `${present} present · ${partial} partial · ${absent} absent`;
  }, [participants, attendance]);

  // Seed the initial collapsed state from completeness so an already-complete
  // section loads collapsed without flashing open first (MED-1077 AC1). The
  // effect then keeps it in sync as completion / dirty state changes.
  const [collapsed, setCollapsed] = useState<boolean>(() =>
    computeIsComplete(participants, attendance, leaderUserId)
  );
  const [dirty, setDirty] = useState<boolean>(false);
  useEffect(() => {
    setCollapsed(isComplete && !dirty);
  }, [isComplete, dirty]);

  const markDirty = () => setDirty(true);

  const save = async () => {
    const ok = await onSave();
    if (ok) setDirty(false);
  };

  return { isComplete, summaryLine, collapsed, setCollapsed, markDirty, save };
}
